// Mirrors server/internal/room/protocol.go and library/types.go.

export type Role = 'owner' | 'controller' | 'viewer'
export type Status = 'paused' | 'playing' | 'waiting'
export type ClientState = 'loading' | 'ready' | 'buffering'

export interface Member {
  userId: string
  nick: string
  /** Picture URL (Discord users); absent for anonymous ones. */
  avatar?: string
  role: Role
  online: boolean
  state?: ClientState
  buffered: number
  catchingUp?: boolean
  /** The owner chose never to wait for this member. */
  noWait?: boolean
}

export interface Playback {
  mediaId?: string
  status: Status
  pos: number
  anchor: number
  seq: number
  waitingFor?: string[]
  loadingSource?: boolean
  /** The loading is the server compressing the video (720p) slower than it plays. */
  encoding?: boolean
  waitSince?: number
}

export interface FileEntry {
  index: number
  path: string
  length: number
  kind: 'video' | 'audio' | 'subtitle' | 'other'
}

export interface TorrentStats {
  peers: number
  seeds: number
  downloadRate: number
  uploadRate: number
  cacheBytes: number
}

export interface Source {
  infoHash: string
  name: string
  status: 'metadata' | 'ready' | 'error'
  error?: string
  files?: FileEntry[]
  progress?: Record<string, number>
  stats?: TorrentStats
  /** The title ("movie:27205") the torrent was added for. */
  forTitle?: string
}

export interface AudioTrack {
  id: string
  name: string
  language?: string
  codec: string
  channels: number
  external: boolean
  default: boolean
}

export interface SubtitleTrack {
  id: string
  name: string
  language?: string
  format: 'ass' | 'vtt' | 'pgs' | 'unsupported'
  external: boolean
  default: boolean
  forced: boolean
}

export interface MediaInfo {
  id: string
  infoHash: string
  fileIndex: number
  name: string
  duration: number
  bitrate: number
  video: {
    codec: string
    profile: string
    pixFmt: string
    width: number
    height: number
    /** Dynamic range: "pq" (HDR10, HDR10+), "hlg", absent for SDR. */
    range?: 'pq' | 'hlg'
    /** Dolby Vision profile; dvCompat is its base layer (1 HDR10, 2 SDR, 4 HLG, 0 none). */
    dolbyVision?: number
    dvCompat?: number
  }
  audio: AudioTrack[]
  subtitles: SubtitleTrack[]
  fonts?: { name: string; size: number }[]
}

export interface Media {
  status: 'analyzing' | 'ready' | 'error'
  error?: string
  info?: MediaInfo
  fileIndex: number
}

export interface ChatMessage {
  id: number
  userId: string
  nick: string
  text: string
  ts: number
}

export interface PlaybackRequest {
  id: number
  userId: string
  nick: string
  kind: 'play' | 'pause' | 'seek'
  pos?: number
  ts: number
}

export type ServerMessage =
  | {
      t: 'welcome'
      you: { userId: string; role: Role }
      room: { id: string; name: string }
      members: Member[]
      playback: Playback
      title: CatalogRef | null
      source: Source | null
      media: Media | null
      chat: ChatMessage[]
      requests?: PlaybackRequest[]
      serverTime: number
      autoplayNext: boolean
    }
  | { t: 'pong'; id: number; server: number }
  | { t: 'members'; members: Member[] }
  | { t: 'playback'; playback: Playback; by?: string; action?: string }
  | { t: 'source'; source: Source | null }
  | { t: 'media'; media: Media | null }
  | { t: 'title'; title: CatalogRef | null }
  | { t: 'subtitle_added'; by: string; name: string }
  | { t: 'chat'; msg: ChatMessage }
  | { t: 'reaction'; userId: string; nick: string; emoji: string }
  | { t: 'request'; request: PlaybackRequest }
  | { t: 'request_resolved'; id: number; accepted: boolean; by?: string; expired?: boolean; gone?: boolean }
  | { t: 'you'; you: { userId: string; role: Role } }
  | { t: 'settings'; autoplayNext: boolean }
  | { t: 'error'; code: string; message: string }
  | { t: 'closed'; reason: 'kicked' | 'deleted' }

export type ClientMessage =
  | { t: 'hello'; nick: string; caps: { hevc: boolean; av1: boolean } }
  | { t: 'ping'; id: number }
  | { t: 'status'; seq: number; state: ClientState; buffered: number; pos: number }
  | { t: 'play' }
  | { t: 'pause' }
  | { t: 'seek'; pos: number }
  | { t: 'request'; kind: 'play' | 'pause' | 'seek'; pos?: number }
  | { t: 'resolve'; reqId: number; accept: boolean }
  | { t: 'force_start' }
  | { t: 'skip_member'; userId: string }
  | { t: 'set_no_wait'; userId: string; on: boolean }
  | { t: 'chat'; text: string }
  | { t: 'react'; emoji: string }
  /** Temporary: a player event for the server log. */
  | { t: 'diag'; text: string }
  | { t: 'set_nick'; nick: string }
  | { t: 'set_role'; userId: string; role: Role }
  | { t: 'kick'; userId: string }
  | { t: 'select_media'; fileIndex: number }
  | { t: 'set_autoplay'; on: boolean }

export const REACTIONS = ['👍', '😂', '😮', '😢', '😡', '❤️', '🔥', '👏', '🎉', '💀']

// Catalog (TMDb) and release search, mirroring server/internal/catalog and
// server/internal/providers.

export type CatalogType = 'movie' | 'tv'

export interface CatalogItem {
  type: CatalogType
  id: number
  title: string
  originalTitle: string
  year?: number
  poster?: string
  rating?: number
  overview?: string
}

export interface CatalogSeason {
  number: number
  name: string
  episodes: number
  year?: number
  poster?: string
}

export interface CatalogDetails extends CatalogItem {
  yearEnd?: number
  tagline?: string
  genres: string[]
  countries: string[]
  runtime?: number
  votes?: number
  backdrop?: string
  seasons?: CatalogSeason[]
  titles: Record<string, string>
}

export interface Quality {
  resolution?: string
  hdr?: boolean
  dv?: boolean
  codec?: string
  source?: string
  disc?: boolean
}

export interface Release {
  provider: string
  id: string
  title: string
  category: string
  size: number
  seeds: number
  leeches: number
  published?: number
  url: string
  quality: Quality
  seasonFrom?: number
  seasonTo?: number
  episodes?: string
}

export interface ReleasesResult {
  releases: Release[]
  errors?: Record<string, string>
  codes?: Record<string, string>
}

/**
 * What a room watches according to the catalog. It is chosen on its own,
 * before or without a release, and is what subtitles are searched for.
 */
export interface CatalogRef {
  type: CatalogType
  id: number
  year?: number
  poster?: string
  backdrop?: string
  titles: Record<string, string>
  season?: number
  imdbId?: string
}

/** A subtitle file found on OpenSubtitles. */
export interface SubtitleResult {
  fileId: number
  fileName: string
  language: string
  release: string
  format: string
  downloads: number
  hearingImpaired: boolean
  machineTranslated: boolean
  fps?: number
  uploaded?: string
  season?: number
  episode?: number
  /** Made for a file with the same moviehash: in sync with this video. */
  hashMatch: boolean
}

export interface SubtitleSearch {
  results: SubtitleResult[]
  byTitle: boolean
  byHash: boolean
}

/** What a release description says about its tracks, as the uploader wrote it. */
export interface ReleaseDetails {
  translation?: string[]
  audio?: string[]
  subtitles?: string[]
}

/** Tracks of the first video of a release, read from the file itself. */
export interface ReleaseTracks {
  file: string
  /** Video files (episodes) in the release. */
  videos: number
  video: MediaInfo['video']
  duration: number
  audio: AudioTrack[]
  subtitles: SubtitleTrack[]
}
